/**
 * 開閉の帯（#244、docs/94 B-5）。帳票の型では帳票の頭のすぐ下、詳細の型では節の見出しに使う。
 *   ・見出しの1行（48px）：絵・見出し・（閉じたときの）要約・右に操作（actions）と「開く ⌄／閉じる ⌃」（aria-expanded）
 *   ・閉じているときは見出しの行の全体が押せる（操作を置かない帯）。操作のある帯は「開く／閉じる」のボタンだけが押せる
 *   ・開閉はこの端末に覚える（localStorage gb.fold.<id>。覚えられなくても動く）。始めの形は defaultOpen。
 *     呼ぶ側が開閉を持つときは open・onToggle を渡す（覚え方は呼ぶ側＝{project}のマップ）
 *   ・empty（0件）のときは見出しの1行だけ（開閉のボタンを出さない）
 *   ・閉じている間は中身を描かない（地図の iframe などを読まない）
 * 載せるもの：{order}の条件・見積書の{vendorQuote}・見積依頼書の送信設定・{project}のマップ。新しく開け閉めする節を作るときもこの部品
 */
import { useState, type ReactNode } from "react";

const KEY = (id: string) => `gb.fold.${id}`;
function readFold(id: string): boolean | null {
  try {
    const v = localStorage.getItem(KEY(id));
    return v === "1" ? true : v === "0" ? false : null;
  } catch {
    return null;
  }
}
function writeFold(id: string, open: boolean) {
  try {
    localStorage.setItem(KEY(id), open ? "1" : "0");
  } catch {
    /* 端末が覚えられなくても動く */
  }
}

/** 開閉の状態（この端末に覚える） */
export function useFoldState(id: string, defaultOpen: boolean): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(() => readFold(id) ?? defaultOpen);
  return [
    open,
    (v) => {
      setOpen(v);
      writeFold(id, v);
    },
  ];
}

export const FOLD_MSG = { open: "開く", close: "閉じる" } as const;

export function FoldBand({
  id,
  icon,
  title,
  summary,
  keepSummary = false,
  actions,
  empty = false,
  defaultOpen = true,
  open: givenOpen,
  onToggle,
  className,
  testId,
  closedTestId,
  toggleTestId,
  openLabel = FOLD_MSG.open,
  closeLabel = FOLD_MSG.close,
  children,
}: {
  /** 覚える鍵（gb.fold.<id>） */
  id: string;
  icon?: ReactNode;
  title: ReactNode;
  /** 閉じたときの要約（見出しの右。開いているときは出さない＝keepSummary で開いても出す） */
  summary?: ReactNode;
  keepSummary?: boolean;
  /** 見出しの右（開閉のボタンの左）に置く操作。あると閉じた見出しの全体は押せない */
  actions?: ReactNode;
  /** 0件：見出しの1行だけ */
  empty?: boolean;
  defaultOpen?: boolean;
  /** 呼ぶ側が開閉を持つとき */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  className?: string;
  testId?: string | undefined;
  /** 閉じた見出しの行（全体が押せるとき）の testid */
  closedTestId?: string;
  /** 開閉のボタンの testid */
  toggleTestId?: string;
  openLabel?: string;
  closeLabel?: string;
  children?: ReactNode;
}) {
  const [own, setOwn] = useFoldState(id, defaultOpen);
  const open = givenOpen ?? own;
  const setOpen = (v: boolean) => (onToggle ? onToggle(v) : setOwn(v));
  const shown = open && !empty;
  const head = (
    <>
      {icon ? (
        <span className="fold-ic" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="fold-t">{title}</span>
      {(!shown || keepSummary) && summary ? <span className="fold-sum">{summary}</span> : null}
      <span className="sp" />
    </>
  );
  const toggle = empty ? null : (
    <span className="fold-tg" aria-hidden={!actions && !shown ? "true" : undefined}>
      {shown ? closeLabel : openLabel} <span aria-hidden="true">{shown ? "⌃" : "⌄"}</span>
    </span>
  );
  return (
    <section className={"fold" + (shown ? " is-open" : "") + (empty ? " is-empty" : "") + (className ? ` ${className}` : "")} data-testid={testId}>
      {!shown && !actions && !empty ? (
        // 閉じていて操作の無い帯：見出しの行の全体が「開く」のボタン
        <button type="button" className="fold-h is-btn" aria-expanded={false} onClick={() => setOpen(true)} data-testid={closedTestId ?? toggleTestId}>
          {head}
          {toggle}
        </button>
      ) : (
        <div className="fold-h">
          {head}
          {actions}
          {empty ? null : (
            <button type="button" className="btn text sm fold-btn" aria-expanded={shown} onClick={() => setOpen(!shown)} data-testid={toggleTestId}>
              {shown ? closeLabel : openLabel} <span aria-hidden="true">{shown ? "⌃" : "⌄"}</span>
            </button>
          )}
        </div>
      )}
      {shown ? <div className="fold-b">{children}</div> : null}
    </section>
  );
}
